<template>
  <div class="table-page container py-4">
    <h1 class="mb-3">Табличный отчёт по занятиям</h1>
    <p class="text-muted mb-4">
      Выберите период и нажмите «Сформировать», чтобы увидеть занятия, сгруппированные по помещению, тренеру и времени, с указанием дней недели.
    </p>

    <form class="row g-3 align-items-end mb-4" @submit.prevent="fetchTable">
      <div class="col-md-4">
        <label class="form-label fw-semibold">Начало периода</label>
        <input v-model="startDate" type="date" class="form-control" required>
      </div>
      <div class="col-md-4">
        <label class="form-label fw-semibold">Конец периода</label>
        <input v-model="endDate" type="date" class="form-control" required>
      </div>
      <div class="col-md-4 d-flex gap-2">
        <button type="submit" class="btn btn-primary w-100" :disabled="loading">
          {{ loading ? 'Загрузка...' : 'Сформировать' }}
        </button>
      </div>
    </form>

    <div class="row g-3 mb-4">
      <div class="col-md-6">
        <label class="form-label fw-semibold">Фильтр по помещениям</label>
        <select v-model="selectedRooms" class="form-select" multiple size="6">
          <option v-for="room in roomOptions" :key="room" :value="room">
            {{ room }}
          </option>
        </select>
        <div class="form-text">Оставьте пустым, чтобы показать все помещения.</div>
      </div>
      <div class="col-md-6 d-flex flex-column gap-3">
        <div>
          <label class="form-label fw-semibold">Фильтр по ДЕТИ</label>
          <select v-model="kidsFilter" class="form-select">
            <option value="all">Все группы</option>
            <option value="only">Только ДЕТИ (детские)</option>
            <option value="exclude">Без ДЕТИ</option>
          </select>
        </div>
        <div class="form-text">Порядок столбцов настраивается стрелками в заголовке таблицы. Сортировка следует этому порядку.</div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <div v-if="!loading && rows.length === 0" class="alert alert-secondary">
      Нет данных за выбранный период.
    </div>

    <div class="table-responsive" v-if="rows.length">
      <table class="table table-bordered align-middle">
        <thead class="table-light">
          <tr>
            <th v-for="(col, idx) in columnsOrder" :key="col" class="align-middle">
              <div class="d-flex align-items-center justify-content-between gap-2">
                <span>{{ columnLabels[col] }}</span>
                <div class="btn-group btn-group-sm">
                  <button type="button" class="btn btn-outline-secondary" :disabled="idx === 0" @click="moveColumn(idx, -1)">↑</button>
                  <button type="button" class="btn btn-outline-secondary" :disabled="idx === columnsOrder.length - 1" @click="moveColumn(idx, 1)">↓</button>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in sortedRows" :key="idx">
            <td v-for="col in columnsOrder" :key="col">
              {{ formatCell(row, col) }}
            </td>
          </tr>
        </tbody>
      </table>
      <div class="text-muted small">Обновлено: {{ fetchedAtHuman || '—' }}</div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'TableReport',
  data() {
    const { start, end } = this.getDefaultRange();
    return {
      startDate: start,
      endDate: end,
      rows: [],
      fetchedAtHuman: '',
      loading: false,
      error: '',
      selectedRooms: [],
      columnsOrder: ['roomTitle', 'exerciseTitle', 'trainerName', 'capacity', 'time', 'days', 'occurrences'],
      columnLabels: {
        roomTitle: 'Помещение',
        exerciseTitle: 'Группа',
        trainerName: 'Тренер',
        capacity: 'Ёмкость группы',
        time: 'Время',
        days: 'Дни недели',
        occurrences: 'Кол-во за период'
      },
      kidsFilter: 'all'
    };
  },
  mounted() {
    this.fetchTable();
  },
  computed: {
    roomOptions() {
      const set = new Set(this.rows.map(r => r.roomTitle || 'Локация не указана'));
      return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'));
    },
    filteredRows() {
      let base = this.rows;
      if (this.selectedRooms.length) {
        const selected = new Set(this.selectedRooms);
        base = base.filter(r => selected.has(r.roomTitle));
      }
      if (this.kidsFilter === 'only') {
        base = base.filter(r => (r.exerciseTitle || '').includes('ДЕТИ'));
      } else if (this.kidsFilter === 'exclude') {
        base = base.filter(r => !(r.exerciseTitle || '').includes('ДЕТИ'));
      }
      return base;
    },
    sortedRows() {
      const arr = [...this.filteredRows];
      const order = this.columnsOrder;
      const cmp = (a, b, col) => {
        const get = (row) => this.rawCell(row, col);
        const va = get(a);
        const vb = get(b);
        if (va == null && vb == null) return 0;
        if (va == null) return -1;
        if (vb == null) return 1;
        if (typeof va === 'number' && typeof vb === 'number') return va - vb;
        return String(va).localeCompare(String(vb), 'ru', { sensitivity: 'base' });
      };
      arr.sort((a, b) => {
        for (const col of order) {
          const res = cmp(a, b, col);
          if (res !== 0) return res;
        }
        return 0;
      });
      return arr;
    }
  },
  methods: {
    getDefaultRange() {
      const today = new Date();
      const day = today.getDay();
      const diffToMonday = (day + 6) % 7;
      const monday = new Date(today);
      monday.setDate(today.getDate() - diffToMonday);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      const toStr = (d) => d.toISOString().slice(0, 10);
      return { start: toStr(monday), end: toStr(sunday) };
    },
    formatDays(days = []) {
      return Array.isArray(days) ? days.join(' ') : '';
    },
    async fetchTable() {
      this.error = '';
      this.loading = true;
      try {
        const start = `${this.startDate}T00:00:00`;
        const end = `${this.endDate}T23:59:59`;
        const url = `${process.env.VUE_APP_API_URL}/schedule/table?startDate=${encodeURIComponent(start)}&endDate=${encodeURIComponent(end)}&useCache=false`;
        const { data } = await axios.get(url);
        this.rows = data?.data || [];
        this.fetchedAtHuman = data?.fetchedAtHuman || '';
      } catch (e) {
        console.error(e);
        this.error = 'Не удалось получить данные. Проверьте соединение или повторите попытку.';
      } finally {
        this.loading = false;
      }
    },
    rawCell(row, col) {
      switch (col) {
        case 'days': return Array.isArray(row.days) ? row.days.join(' ') : '';
        case 'occurrences': return row.occurrences ?? 0;
        default: return row[col];
      }
    },
    formatCell(row, col) {
      if (col === 'capacity') return row.capacity ?? '—';
      if (col === 'days') return Array.isArray(row.days) ? row.days.join(' ') : '';
      if (col === 'occurrences') return row.occurrences ?? 0;
      return row[col] ?? '';
    },
    moveColumn(idx, delta) {
      const target = idx + delta;
      if (target < 0 || target >= this.columnsOrder.length) return;
      const arr = [...this.columnsOrder];
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      this.columnsOrder = arr;
    }
  }
};
</script>

<style scoped>
.table-page {
  text-align: left;
}

.table {
  background: #fff;
}
</style>

